import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Box, Typography, Grid, Button, CircularProgress } from "@mui/material";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import { toast } from "react-toastify";
import { PaymentService } from "../../../services/PaymentService";
import { MOP_INFO } from "../../../constants/paymentMaps";
import { openPaymentPopup } from "../../../utils/openPaymentPopup";

const WalletSection = ({ mops, txn, onPaymentInitiated }) => {
  const navigate = useNavigate();
  const [selectedWallet, setSelectedWallet] = useState("");
  const [submitting, setSubmitting]         = useState(false);

  const handlePaymentFailure = (message) => {
    navigate("/response-page", {
      replace: true,
      state: {
        response: {
          status:          "FAILED",
          responseMessage: message,
          txnId:           txn.transactionId,
          orderId:         txn.orderId,
          amount:          txn.amount,
        },
      },
    });
  };

  const handlePay = async () => {
    if (!selectedWallet) { toast.warning("Please select a wallet"); return; }
    setSubmitting(true);
    try {
      const res = await PaymentService.submitPayReq({
        orderId:         txn.transactionId,
        paymentType:     "WL",
        mopType:         selectedWallet,
        vpa: "", cardType: "", cardNumber: "",
        cardExpiryMonth: "", cardExpiryYear: "",
        cardSecurity: "", cardHolderName: "",
      });
      if (res.statusCode === 200) {
        const link = res.paymentLink || res.intentLink;
        const popup = link ? openPaymentPopup(link) : null;
        onPaymentInitiated("wallet", popup);
      } else {
        toast.error(res.message || "Payment request failed");
        handlePaymentFailure(res.message || "Payment request failed");
      }
    } catch (err) {
      console.error("Wallet payment error:", err);
      const msg = "An error occurred";
      toast.error(msg);
      handlePaymentFailure(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const selectedLabel = MOP_INFO[selectedWallet]?.label || selectedWallet;

  return (
    <Box>
      <Typography variant="h6" fontWeight={600} color="#1f2937" mb={2}>
        Select Wallet
      </Typography>

      <Grid container spacing={1.5}>
        {mops.map((wallet) => {
          const info = MOP_INFO[wallet] || { label: wallet, logo: null };
          const isSelected = selectedWallet === wallet;
          return (
            <Grid item xs={6} sm={4} key={wallet}>
              <Box
                onClick={() => setSelectedWallet(wallet)}
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  cursor: "pointer",
                  p: 2,
                  borderRadius: "12px",
                  border: `2px solid ${isSelected ? "#15b86d" : "#e5e7eb"}`,
                  bgcolor: isSelected ? "#f0fdf4" : "white",
                  transition: "all 0.2s ease",
                  "&:hover": { borderColor: "#15b86d", transform: "translateY(-2px)", boxShadow: "0 4px 12px rgba(21,184,109,0.15)" },
                }}
              >
                {info.logo ? (
                  <Box
                    component="img"
                    src={info.logo}
                    alt={info.label}
                    sx={{ height: 38, objectFit: "contain", mb: 1 }}
                  />
                ) : (
                  <AccountBalanceWalletIcon
                    sx={{ fontSize: 38, color: isSelected ? "#15b86d" : "#9ca3af", mb: 1 }}
                  />
                )}
                <Typography variant="caption" fontWeight={600} color={isSelected ? "#15b86d" : "#374151"}>
                  {info.label}
                </Typography>
              </Box>
            </Grid>
          );
        })}
      </Grid>

      <Box mt={3}>
        <Button
          fullWidth
          variant="contained"
          onClick={handlePay}
          disabled={!selectedWallet || submitting}
          sx={{
            py: 1.5, fontWeight: 600, borderRadius: "10px",
            textTransform: "none", fontSize: "1rem",
            background: "#15b86d",
            "&:hover": { background: "#13a360" },
            "&:disabled": { background: "#e5e7eb", color: "#9ca3af" },
          }}
        >
          {submitting
            ? <CircularProgress size={22} sx={{ color: "white" }} />
            : selectedWallet
              ? `Pay ₹${parseFloat(txn.amount || 0).toFixed(2)} via ${selectedLabel}`
              : `Pay ₹${parseFloat(txn.amount || 0).toFixed(2)}`}
        </Button>
      </Box>
    </Box>
  );
};

export default WalletSection;
